import { useMemo } from 'react'
import { RecommendedAvailabilitiesProps, useRecommendedAvailabilities } from '../useRecommendedAvailabilities'
import filterSlotsByResourceTypes, { type ShopIdToIsCabinOnly } from '../../util/filterSlotsByResourceTypes'

/**
 * Fetches availabilities for the given params (via useRecommendedAvailabilities)
 * and filters the result so that only valid combined or cabin-only slots are
 * returned (via filterSlotsByResourceTypes). Callers are responsible for
 * providing the correct resources (e.g. from useProviderRecommendations).
 *
 * Optional params.shopIdToIsCabinOnly: when provided, filtering is per-shop.
 * Shops with shopIdToIsCabinOnly[shopId] === true are treated as CABIN_ONLY (no
 * fitter–cabin pairing; cabin-only slots are exposed). Other shops keep default
 * pairing behavior.
 */
export type CombinedResourceAvailabilitiesParams = RecommendedAvailabilitiesProps & {
  shopIdToIsCabinOnly?: ShopIdToIsCabinOnly
}

export const useCombinedResourceAvailabilities = (params: CombinedResourceAvailabilitiesParams) => {
  const { resources, shopIdToIsCabinOnly } = params

  const { data: availabilities, isLoading: isAvailabilitiesLoading, ...rest } = useRecommendedAvailabilities(params)

  const filteredAvailabilities = useMemo(
    () => filterSlotsByResourceTypes(availabilities, resources ?? [], shopIdToIsCabinOnly),
    [availabilities, resources, shopIdToIsCabinOnly]
  )

  return {
    ...rest,
    data: filteredAvailabilities,
    isLoading: isAvailabilitiesLoading,
  }
}
